import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AddIcon from '@material-ui/icons/Add';
import { CreateWarehouseCardMutation, StockCardsSearchQuery, UnitType, useStockCardsSearchQuery } from '../../../../generated/graphql';
import { useDebounce } from '../../../../utils';
import { Typography } from '../../../../ui-kit';
import ProductCreateWarehouseCard from '../ProductCreateWarehouseCard/ProductCreateWarehouseCard';
import { AutocompleteSearch, CREATE_NEW_STRING, FETCH_MORE_STRING } from '../../../../components/forms/fields';

type StockCards = StockCardsSearchQuery['stockCards'];
export type WarehouseSearchStockCard = StockCardsSearchQuery['stockCards'][0];
interface OptionProps {
    isLoading: boolean;
    option: Pick<WarehouseSearchStockCard, 'id' | 'stockCardCategory' | 'name'>;
}
const Option = ({ isLoading, option }: OptionProps) => {
    const { t } = useTranslation();
    const { id, stockCardCategory, name } = option;
    if (id === CREATE_NEW_STRING) {
        return (
            <Box display='flex'>
                <AddIcon color='primary' />
                <Box mr={0.5} />
                <Typography color='primary'>{t('New card')}</Typography>
            </Box>
        );
    }
    if (id === FETCH_MORE_STRING) {
        return isLoading
            ? (
                <Box display='flex' flexGrow={1} justifyContent='center'>
                    <CircularProgress size={24} />
                </Box>
            ): <Typography>{t('Show more')}</Typography>;
    }

    const categoryName = stockCardCategory?.name;
    const postfix = categoryName ?? t('Unassigned category');

    return (
        <Box display='flex' flexDirection='column'>
            <Typography>{name}</Typography>
            <Typography color='textSecondary'>{postfix}</Typography>
        </Box>
    );
};

const LIMIT = 10;

export interface WarehouseCardSearchProps {
    warehouseIds: string[];
    name: string;
    onItemClick: (stockCard: WarehouseSearchStockCard | null) => void;
    onlyPieces?: boolean;
    value?: string;
    currentWarehouseCard?: WarehouseSearchStockCard | null;
    helperText?: string;
    label?: string;
    onBlur?(event: React.FocusEvent<HTMLDivElement>): void;
}

const getOptionLabel = (option: WarehouseSearchStockCard) => option.name || '';

const WarehouseCardSearch = ({
    warehouseIds,
    onItemClick: onSearchResultClick,
    value: currentValue,
    currentWarehouseCard,
    helperText,
    label,
    onlyPieces,
    onBlur,
}: WarehouseCardSearchProps) => {
    const [isNewCardScreenShown, setIsNewCardScreenShown] = useState(false);
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const debouncedSearchQuery = useDebounce(searchQuery);
    const storageUnit = onlyPieces ? UnitType.Pcs : undefined;
    const { data, loading, fetchMore, refetch } = useStockCardsSearchQuery({
        variables: {
            nameOrSku: debouncedSearchQuery,
            warehouseIds,
            offset,
            limit: LIMIT,
            storageUnit,
        },
        nextFetchPolicy: 'cache-first'
    });

    let stockCards: StockCards = data ? data.stockCards : [];

    useEffect(() => {
        setOffset(0);
        // eslint-disable-next-line no-void
        void refetch( { nameOrSku: debouncedSearchQuery, offset: 0 });
    }, [debouncedSearchQuery, refetch]);

    if (data) {
        ({ stockCards } = data);
    }

    useEffect(() => {
        if (offset === 0) {
            if (offset === 0 && stockCards.length % LIMIT !== 0) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }
        }
    }, [stockCards, offset]);

    let finalValue = currentValue ? stockCards.find(( { id }) => currentValue === id) : null;
    if (!finalValue && currentValue) {
        finalValue = currentWarehouseCard;
    }

    // eslint-disable-next-line consistent-return
    const handleItemClick = (_event: unknown, value: WarehouseSearchStockCard | null) => onSearchResultClick(value);

    const onSubmitCallback = ({ createStockCard }: CreateWarehouseCardMutation) => onSearchResultClick(createStockCard);

    const handleFetchMore = async () => {
        if (fetchMore) {
            const newOffset = offset + LIMIT;
            setIsFetchingMore(true);
            const { data: newData } = await fetchMore({ variables: { offset: newOffset } });
            setIsFetchingMore(false);
            setOffset(newOffset);

            if (newData.stockCards.length < LIMIT) {
                setHasMore(false);
            }
        }
    };

    const onCreateNew = () => setIsNewCardScreenShown(true);

    const isLoading = isFetchingMore || loading;

    return (
        <>
            {isNewCardScreenShown && (
                <ProductCreateWarehouseCard
                    onlyPieces={onlyPieces}
                    onMutationFinished={onSubmitCallback}
                    warehouseId={warehouseIds[0]}
                    onClose={() => {
                        return setIsNewCardScreenShown(false);
                    }}
                />
            )}
            <AutocompleteSearch
                label={label}
                fullWidth
                onFetchMore={handleFetchMore}
                onCreateNew={onCreateNew}
                showFetchMore={hasMore}
                onBlur={onBlur}
                helpertext={helperText}
                options={stockCards}
                isLoading={isLoading}
                renderOption={(option: WarehouseSearchStockCard) => <Option option={option} isLoading={isLoading} />}
                getOptionLabel={getOptionLabel}
                value={finalValue}
                onSearchResultClick={handleItemClick}
                searchQuery={searchQuery}
                onInputChange={setSearchQuery}
            />
        </>
    );
};

export default WarehouseCardSearch;